import { type EngineContext } from '../core/EngineContext';
import { SystemStage, type System } from '../core/System';
import { type InputManager } from '../input/InputManager';
import { type Scene } from '../scene/Scene';
import { type Unsubscribe } from '../utils/Unsubscribe';

/**
 * Turns the events queued since the last frame into this frame's input state.
 *
 * The first thing that runs in a frame, and the reason every behavior in that frame agrees about where
 * the pointer is. It also converts the pointer into world units, which is why it needs the scene: the
 * conversion goes through the scene's own camera, so two scenes with different cameras each get the
 * right answer with no global to keep in step.
 *
 * Activating a scene clears whatever was held. Otherwise a finger still down through a scene change, or
 * a key held while jumping to another screen, arrives in the new scene as input nobody there gave.
 *
 * Losing a pointer lock is the same bug wearing a different hat: the escape key that ends a lock leaves
 * the mouse button down and no release ever arrives, so every pointer still held is canceled.
 */
export class InputSystem implements System {
  /** Input is drained before anything reads it. */
  readonly stage = SystemStage.Input;

  readonly #input: InputManager;
  readonly #subscriptions: Unsubscribe[] = [];

  constructor(input: InputManager) {
    this.#input = input;
  }

  /** Starts dropping held input on every scene activation and every lost pointer lock. */
  onRegister(context: EngineContext): void {
    this.#subscriptions.push(
      context.events.on('sceneActivated', () => {
        this.#input.reset();
      }),
      context.events.on('pointerLockChanged', ({ isLocked }) => {
        if (!isLocked) {
          this.#input.cancelPointers();
        }
      }),
    );
  }

  /** Drains the input queues into frame state. */
  onUpdate(scene: Scene, _deltaSeconds: number): void {
    this.#input.beginFrame(scene.camera);
  }

  /** Stops listening for scene activations and lock changes. */
  onUnregister(): void {
    for (const unsubscribe of this.#subscriptions) {
      unsubscribe();
    }
    this.#subscriptions.length = 0;
  }
}
